import { Controller } from "@hotwired/stimulus"
import ToastController from "@controllers/toast_controller"

// Connects to data-controller="settings-form"
export default class extends Controller {
  static targets = ["submitButton"]

  connect() {
    console.log("Settings form controller connected")

    // Store original values
    this.originalValues = {}
    this.trackInputs()

    // Disable submit button initially
    this.updateSubmitButton()

    // Initialize toast controller
    this.toast = new ToastController()
  }

  trackInputs() {
    const inputs = this.element.querySelectorAll('input[name]:not([type="hidden"]), textarea[name]')
    inputs.forEach(input => {
      // Store original value
      this.originalValues[input.name] = input.value || ''

      // Add change listener
      input.addEventListener('input', () => this.checkForChanges())
      input.addEventListener('change', () => this.checkForChanges())
    })
  }

  checkForChanges() {
    const inputs = this.element.querySelectorAll('input[name]:not([type="hidden"]), textarea[name]')
    let hasChanges = false

    inputs.forEach(input => {
      const currentValue = input.value || ''
      const originalValue = this.originalValues[input.name] || ''

      if (currentValue !== originalValue) {
        hasChanges = true
      }
    })

    this.updateSubmitButton(hasChanges)
  }

  updateSubmitButton(enabled = false) {
    const submitButton = this.element.querySelector('button[type="submit"]')
    if (submitButton) {
      submitButton.disabled = !enabled
      // The Ui::Button::Component handles disabled styling automatically
      // No need to manually add/remove classes
    }
  }

  submit(event) {
    event.preventDefault()

    // Get CSRF token
    const token = document.querySelector('meta[name="csrf-token"]').content

    // Collect changed fields using FormData to support nested params
    const inputs = this.element.querySelectorAll('input[name]:not([type="hidden"]), textarea[name]')
    const formData = new FormData()
    let hasChanges = false
    let profileAttributesChanged = false

    inputs.forEach(input => {
      const isFileInput = input.type === 'file'
      const currentValue = input.value || ''
      const originalValue = this.originalValues[input.name] || ''
      const changed = isFileInput ? (input.files && input.files.length > 0) : (currentValue !== originalValue)

      // Only include changed fields
      if (changed) {
        if (isFileInput) {
          if (input.files && input.files.length > 0) {
            formData.append(input.name, input.files[0])
          }
        } else {
          formData.append(input.name, currentValue)
        }

        if (input.name.startsWith('user[facilitator_profile_attributes][') &&
            input.name !== 'user[facilitator_profile_attributes][id]') {
          profileAttributesChanged = true
        }

        hasChanges = true
      }
    })

    // Keep nested profile update attached to the existing record.
    // Without id, Rails may treat this as a new nested record update.
    if (profileAttributesChanged) {
      const profileIdInput = this.element.querySelector('input[type="hidden"][name="user[facilitator_profile_attributes][id]"]')
      if (profileIdInput && profileIdInput.value) {
        formData.append(profileIdInput.name, profileIdInput.value)
      }
    }

    // Don't submit if no changes
    if (!hasChanges) {
      console.log('No changes to save')
      return
    }

    // Disable button during submit
    this.updateSubmitButton(false)

    // Submit via Fetch API with FormData (supports nested params and file uploads)
    fetch(this.element.action, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': token,
        'Accept': 'application/json'
      },
      body: formData,
      credentials: 'same-origin'
    })
    .then(response => {
      if (response.ok) {
        // Update original values to current values (so button stays disabled)
        inputs.forEach(input => {
          this.originalValues[input.name] = input.value || ''
        })

        // Show success toast
        this.toast.success('Settings updated successfully')

        // Keep button disabled since no changes now
        this.updateSubmitButton(false)
      } else {
        return response.json().then(data => {
          const errorMessage = data.errors ? data.errors.join(', ') : 'Failed to update settings'
          this.toast.error(errorMessage)
          // Re-enable button on error
          this.updateSubmitButton(true)
        })
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.toast.error('An error occurred while saving settings. Please try again.')
      // Re-enable button on error
      this.updateSubmitButton(true)
    })
  }
}
